import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { DraftOrder, DraftOrderBlocker } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { emptyInvoiceForm, toInvoiceBody } from '../api/mappers';
import { useDraftInvoicePreview } from '../hooks/use-draft-invoice-preview';
import { useSendDraftInvoice } from '../hooks/use-draft-order-mutations';
import {
  blockersFromConflict,
  isStateConflict,
  needsRecipient,
} from '../schemas/draft-order-rules';
import {
  invoiceSchema,
  type InvoiceFormInput,
  type InvoiceFormOutput,
} from '../schemas/invoice-schema';

import { BlockerList } from './blocker-list';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['to', 'message']);
const PREVIEW_DELAY_MS = 400;

function useDebounced(value: string, delay: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/** The email as the backend renders it now, template override included. */
function InvoicePreview({
  storeId,
  draftId,
  message,
  to,
}: {
  storeId: string;
  draftId: string;
  message: string;
  to: string;
}) {
  const { t } = useTranslation('draftOrders');
  const preview = useDraftInvoicePreview(storeId, draftId, message);
  if (preview.isError) {
    return <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />;
  }
  if (!preview.data) return <Skeleton className="h-64" />;
  return (
    <section aria-label={t('invoice.preview')} className="flex flex-col gap-2">
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-muted-foreground">{t('invoice.previewTo')}</dt>
        <dd className="truncate">
          {to !== '' ? to : (preview.data.to ?? t('invoice.noRecipient'))}
        </dd>
        <dt className="text-muted-foreground">{t('invoice.previewSubject')}</dt>
        <dd className="font-medium">{preview.data.subject}</dd>
      </dl>
      <iframe
        title={t('invoice.previewFrame')}
        sandbox=""
        srcDoc={preview.data.html}
        className="h-72 w-full rounded-md border bg-white"
      />
    </section>
  );
}

export interface SendInvoiceDialogProps {
  storeId: string;
  draft: DraftOrder;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Emails the quote. The recipient defaults to the draft's email and is required when the draft
 * has none (a walk-in draft can still be invoiced to an address typed here). A 409 lists
 * `meta.blockers`: something changed since the page loaded.
 */
export function SendInvoiceDialog({ storeId, draft, open, onOpenChange }: SendInvoiceDialogProps) {
  const { t } = useTranslation(['draftOrders', 'common']);
  const message = useErrorMessage();
  const send = useSendDraftInvoice(storeId);
  const [blockers, setBlockers] = useState<DraftOrderBlocker[] | null>(null);
  const requiresTo = needsRecipient(draft);
  const schema = useMemo(() => invoiceSchema(requiresTo), [requiresTo]);
  const form = useForm<InvoiceFormInput, unknown, InvoiceFormOutput>({
    resolver: useI18nResolver(schema),
    defaultValues: emptyInvoiceForm(draft),
  });
  const typedMessage = useDebounced(form.watch('message').trim(), PREVIEW_DELAY_MS);
  const to = form.watch('to').trim();

  const close = () => {
    onOpenChange(false);
    send.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    setBlockers(null);
    try {
      await send.mutateAsync({ id: draft.id, body: toInvoiceBody(values, draft) });
      close();
    } catch (error) {
      const listed = blockersFromConflict(error);
      if (listed && listed.length > 0) {
        setBlockers(listed);
        return;
      }
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', {
        type: 'server',
        message: isStateConflict(error) ? t('draftOrders:errors.locked') : message(error),
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('draftOrders:invoice.title', { name: draft.name })}</DialogTitle>
          <DialogDescription>{t('draftOrders:invoice.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {blockers ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>
                  <p>{t('draftOrders:invoice.blocked')}</p>
                  <BlockerList blockers={blockers} />
                </AlertDescription>
              </Alert>
            ) : null}
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="to"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('draftOrders:invoice.to')}</FormLabel>
                  <FormControl>
                    <Input type="email" autoComplete="off" {...field} />
                  </FormControl>
                  <FormDescription>
                    {requiresTo
                      ? t('draftOrders:invoice.toRequiredHint')
                      : t('draftOrders:invoice.toHint')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('draftOrders:invoice.message')}</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={3}
                      placeholder={t('draftOrders:invoice.messagePlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <InvoicePreview
              storeId={storeId}
              draftId={draft.id}
              message={typedMessage.slice(0, 2000)}
              to={to}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={send.isPending}>
                {send.isPending
                  ? t('draftOrders:invoice.sending')
                  : draft.status === 'invoice_sent'
                    ? t('draftOrders:invoice.resend')
                    : t('draftOrders:invoice.send')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
